import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTokens } from 'theme';
import { parseMarkdown } from './markdown';
import MathText from './MathText';

const MONO = "ui-monospace, 'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, monospace";

/** Heading levels 1–3 sit inside a message: subtitle-sized h3–h5. */
const HEADING_SIZE = { 1: '1.05rem', 2: '0.98rem', 3: '0.9rem' };

/**
 * The model's answer with its markdown rendered (see `markdown.js` for the
 * subset). Answer HTML is never interpreted; formulas use a separate KaTeX
 * renderer, with no links or external resources.
 */
export default React.memo(function MarkdownText({ text }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  if (blocks.length === 0) return null;

  return (
    <Typography
      component="div"
      variant="body2"
      data-markdown="true"
      sx={{ overflowWrap: 'anywhere', lineHeight: 1.6, '& strong': { fontWeight: 700 } }}
    >
      <Blocks blocks={blocks} />
    </Typography>
  );
});

function Blocks({ blocks, tight = false }) {
  const t = useTokens();
  const gap = tight ? 0.5 : 1.25;
  return blocks.map((block, i) => {
    const mb = i === blocks.length - 1 ? 0 : gap;
    switch (block.type) {
      case 'math':
        return <Box key={i} sx={{ mb }}><MathText text={block.text} display={block.display} source={block.source} /></Box>;
      case 'paragraph':
        return (
          <Box key={i} component="p" sx={{ m: 0, mb, whiteSpace: 'pre-wrap' }}>
            {inline(block.children)}
          </Box>
        );
      case 'heading':
        return (
          <Box
            key={i}
            component={`h${block.level + 2}`}
            sx={{
              m: 0,
              mt: i === 0 ? 0 : 1.5,
              mb: mb === 0 ? 0 : 0.75,
              fontSize: HEADING_SIZE[block.level],
              fontWeight: 700,
              lineHeight: 1.4,
              color: t.ink,
            }}
          >
            {inline(block.children)}
          </Box>
        );
      case 'list':
        return (
          <Box
            key={i}
            component={block.ordered ? 'ol' : 'ul'}
            start={block.ordered ? block.start : undefined}
            sx={{ m: 0, mb, pl: 3 }}
          >
            {block.items.map((item, k) => (
              <Box
                key={k}
                component="li"
                value={block.ordered ? item.number : undefined}
                sx={{ mb: k === block.items.length - 1 ? 0 : 0.5, '&::marker': { color: t.inkMuted } }}
              >
                <Blocks blocks={item.blocks} tight />
              </Box>
            ))}
          </Box>
        );
      case 'codeblock':
        return (
          <Box
            key={i}
            component="pre"
            sx={{
              m: 0,
              mb,
              p: 1.25,
              overflowX: 'auto',
              whiteSpace: 'pre',
              overflowWrap: 'normal',
              bgcolor: t.raised,
              color: t.ink,
              border: `1px solid ${t.border}`,
              borderRadius: '6px',
              fontFamily: MONO,
              fontSize: '0.8rem',
              lineHeight: 1.5,
            }}
          >
            <code>{block.text}</code>
          </Box>
        );
      case 'table':
        return (
          <TableContainer
            key={i}
            sx={{ mb, width: 'fit-content', maxWidth: '100%', overflowX: 'auto', boxShadow: 'none' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {block.head.map((cell, c) => (
                    <TableCell key={c} align={block.align[c] || undefined}>
                      {inline(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {block.rows.map((row, r) => (
                  <TableRow key={r}>
                    {row.map((cell, c) => (
                      <TableCell key={c} align={block.align[c] || undefined}>
                        {inline(cell)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 'hr':
        return (
          <Box
            key={i}
            component="hr"
            sx={{ border: 0, borderTop: `1px solid ${t.border}`, m: 0, my: 1.5 }}
          />
        );
      case 'blockquote':
        return (
          <Box
            key={i}
            component="blockquote"
            sx={{ m: 0, mb, pl: 1.5, borderLeft: `3px solid ${t.border}`, color: t.inkMuted }}
          >
            <Blocks blocks={block.blocks} tight={tight} />
          </Box>
        );
      default:
        return null;
    }
  });
}

function inline(nodes) {
  return nodes.map((node, i) => {
    switch (node.type) {
      case 'math':
        return <MathText key={i} text={node.text} display={node.display} source={node.source} />;
      case 'text':
        return <React.Fragment key={i}>{node.text}</React.Fragment>;
      case 'br':
        return <br key={i} />;
      case 'strong':
        return <strong key={i}>{inline(node.children)}</strong>;
      case 'em':
        return <em key={i}>{inline(node.children)}</em>;
      case 'code':
        return <InlineCode key={i}>{node.text}</InlineCode>;
      default:
        return null;
    }
  });
}

function InlineCode({ children }) {
  const t = useTokens();
  return (
    <Box
      component="code"
      sx={{
        fontFamily: MONO,
        fontSize: '0.85em',
        px: 0.5,
        py: '1px',
        borderRadius: '4px',
        bgcolor: t.raised,
        color: t.ink,
      }}
    >
      {children}
    </Box>
  );
}
